import {
  EDES_MILESTONE_CATALOG_ID,
  EDES_MILESTONE_ROWS,
  sortEdesMilestoneRows,
} from "../data/edesMilestoneCatalog";

const API_BASE = import.meta.env.VITE_API_BASE || "https://u5aw5omfkd.execute-api.us-east-2.amazonaws.com/dev";
const USE_MOCK = import.meta.env.VITE_USE_MOCK === "true";
const LOCAL_KEY_PREFIX = "projectMilestoneRun";

function buildUrl(path, params = {}) {
  const url = new URL(`${API_BASE}${path}`);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, value);
    }
  });
  return url;
}

function isEmptyRun(data) {
  if (!data || typeof data !== "object") return true;
  return Object.keys(data).length === 0;
}

function localRunKey(projectId, runDate) {
  return `${LOCAL_KEY_PREFIX}:${projectId}:${runDate}`;
}

function pickLatestRun(items) {
  if (!Array.isArray(items) || items.length === 0) return null;
  const sorted = [...items]
    .filter(item => item && item.run_date)
    .sort((a, b) => String(a.run_date).localeCompare(String(b.run_date)));
  return sorted[sorted.length - 1] || null;
}

function getLocalRun(projectId, runDate) {
  if (!projectId || !runDate || !window?.localStorage) return null;
  const stored = localStorage.getItem(localRunKey(projectId, runDate));
  return stored ? JSON.parse(stored) : null;
}

function listLocalRuns(projectId) {
  if (!projectId || !window?.localStorage) return [];
  const prefix = `${LOCAL_KEY_PREFIX}:${projectId}:`;
  const items = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith(prefix)) continue;
    const stored = localStorage.getItem(key);
    if (stored) {
      try {
        items.push(JSON.parse(stored));
      } catch (error) {
        console.warn("project milestone local parse error", error);
      }
    }
  }
  return items;
}

function saveLocalRun(payload) {
  if (!payload?.project_id || !payload?.run_date || !window?.localStorage) return;
  localStorage.setItem(localRunKey(payload.project_id, payload.run_date), JSON.stringify(payload));
}

function normalizeCatalogResponse(data) {
  const items = Array.isArray(data?.items) && data.items.length > 0 ? data.items : EDES_MILESTONE_ROWS;
  return { items: sortEdesMilestoneRows(items) };
}

export async function fetchProjectMilestoneCatalog({ catalog_id = EDES_MILESTONE_CATALOG_ID } = {}) {
  if (USE_MOCK) {
    return normalizeCatalogResponse({ items: EDES_MILESTONE_ROWS });
  }

  try {
    const res = await fetch(buildUrl("/project-milestones", { catalog_id }));
    if (!res.ok) throw new Error(`Catalog API error: ${res.status}`);
    return normalizeCatalogResponse(await res.json());
  } catch (error) {
    console.warn("project milestone catalog fallback", error);
    return normalizeCatalogResponse({ items: EDES_MILESTONE_ROWS });
  }
}

export async function fetchProjectMilestoneRun({ project_id, run_date }) {
  if (!project_id) return {};

  const localExact = run_date ? getLocalRun(project_id, run_date) : null;
  const localLatest = pickLatestRun(listLocalRuns(project_id));

  if (USE_MOCK) {
    if (localExact) return localExact;
    if (run_date && localLatest) return { ...localLatest, _loaded_from_latest: true, _data_source: "local" };
    return {};
  }

  try {
    const res = await fetch(buildUrl("/project-milestones", { project_id, run_date }));
    if (!res.ok) throw new Error(`Run API error: ${res.status}`);
    const data = await res.json();
    if (!isEmptyRun(data) || !run_date) return data;

    const listRes = await fetch(buildUrl("/project-milestones", { project_id }));
    if (!listRes.ok) throw new Error(`Run API error: ${listRes.status}`);
    const listData = await listRes.json();
    const latest = pickLatestRun(listData.items);
    if (latest) return { ...latest, _loaded_from_latest: true };
  } catch (error) {
    console.warn("project milestone run fallback", error);
  }

  if (localExact) return { ...localExact, _data_source: "local" };
  if (run_date && localLatest) return { ...localLatest, _loaded_from_latest: true, _data_source: "local" };
  return {};
}

export async function saveProjectMilestoneRun(payload) {
  if (!payload?.project_id || !payload?.run_date) {
    throw new Error("project_id and run_date are required");
  }

  if (USE_MOCK) {
    saveLocalRun(payload);
    return { success: true, _data_source: "local" };
  }

  try {
    const res = await fetch(`${API_BASE}/project-milestones`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`Run save error: ${res.status}`);
    const data = await res.json();
    saveLocalRun(payload);
    return data;
  } catch (error) {
    console.warn("project milestone save fallback", error);
    saveLocalRun(payload);
    return { success: true, _data_source: "local" };
  }
}
